import Tooltip from "@components/Tooltip";
import TnxHistory from "@components/Withdraw/TnxHistory";
import { CLAIM_CTA } from "@constants/analytics";
import { IS_STAKING_PAUSED, toFixedWithoutRounding } from "@constants/common";
import { IS_CONTRACT_UPGRADE_IN_PROGRESS } from "@constants/upgradeFlags";
import { EVENTS } from "@constants/reduxEvents";
import useBufferPool from "@hooks/useBufferPool";
import useDappConfig from "@hooks/useDappConfig";
import useTransactionHistory from "@hooks/useTransactionHistory";
import useWithdrawals from "@hooks/useWithdrawals";
import { Button, Typography } from "@kelp-dao/kernel-design-system";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import moment from "moment";
import { useCallback, useMemo, useState } from "react";
import { emitReduxEvent, trackEvent } from "utils";
import { Hash, formatEther } from "viem";
import { useAccount, useBlockNumber, useSwitchChain } from "wagmi";

const initialCurrentWithdraw = {
  asset: "",
  nonce: 0n,
};

const Withdraw = () => {
  const { data: blockNumber } = useBlockNumber({
    query: { refetchInterval: 10_000 },
  });
  const { isConnected } = useAccount();
  const [currentWithdraw, setCurrentWithdraw] = useState(
    initialCurrentWithdraw
  );

  const { switchChain } = useSwitchChain();
  const { openConnectModal } = useConnectModal();

  const { isWithdrawSupported, withdrawEnabledChains } = useDappConfig();
  const { bufferPool } = useBufferPool();

  const {
    userWithdrawals,
    isFetching,
    firstAvailableWithdrawalNonce,
    refetchWithdrawals,
  } = useWithdrawals();

  const { completedWithdrawals, isFetching: isFetchingCompletedWithdrawals } =
    useTransactionHistory();

  /* Active / Completed tabs */
  const TABS = { active: "Active", completed: "Completed" };
  const [showCompleted, setShowCompleted] = useState(false);

  const handleTabToggle = (option: string) => {
    setShowCompleted(option === TABS.completed);
  };

  // Function to determine if withdrawal would be instant based on buffer pool
  const getWithdrawalTimeEstimate = (withdrawal: any) => {
    const withdrawalAmount = Number(
      formatEther(withdrawal.expectedAssetAmount)
    );
    const bufferPoolForAsset = bufferPool[withdrawal.symbol] || 0;

    // If withdrawal amount is less than or equal to buffer pool, it's instant
    if (withdrawalAmount <= bufferPoolForAsset) {
      return { text: "3-4 days", isInstant: true };
    }

    // Otherwise, use the calculated time
    const finalDuration =
      (withdrawal.unlockedOnBlockNumber - Number(blockNumber)) * 12;
    const initialDuration =
      (withdrawal.initialUnlockDay - Number(blockNumber)) * 12;

    const finalDurationText = moment.duration(finalDuration, "seconds");
    const initialDurationText = moment.duration(initialDuration, "seconds");

    if (finalDurationText.days() <= 0) {
      return { text: "awaiting unlock", isInstant: false };
    }

    return {
      text: `~in ${
        finalDurationText.days() > 1
          ? `${
              initialDurationText.days() <= 0 ? 1 : initialDurationText.days()
            } -`
          : ""
      }${finalDurationText.days()} day${
        finalDurationText.days() > 1 ? "s" : ""
      }`,
      isInstant: false,
    };
  };

  const handleClick = useCallback(
    (amount: number, asset: Hash, nonce: bigint) => {
      if (!isWithdrawSupported) {
        switchChain({ chainId: withdrawEnabledChains[0].id });
      } else {
        trackEvent(CLAIM_CTA, {
          amount: amount,
          asset: asset,
        });

        setCurrentWithdraw({ asset, nonce });

        emitReduxEvent(EVENTS.HANDLE_CLAIM, {
          amount,
          asset,
          callback: () => {
            setCurrentWithdraw(initialCurrentWithdraw);
            refetchWithdrawals();
          },
        });
      }
    },
    [
      isWithdrawSupported,
      refetchWithdrawals,
      switchChain,
      withdrawEnabledChains,
    ]
  );

  const renderPendingWithdrawal = () => {
    return (
      <div>
        <Typography
          type="body"
          variant="xsmall"
          className="text-text-secondary tracking-wide font-medium mb-4 py-2"
        >
          Amount
        </Typography>
        <div className="max-h-[398px] overflow-y-auto">
          {userWithdrawals
            .sort((a, b) => a.startTime - b.startTime)
            .map((withdrawal) => {
              const isClaimable = withdrawal.isUnlocked;

              const hasWithdrawDelayLapsed =
                withdrawal.totalWithdrawDelay < Number(blockNumber);

              const isDisabled =
                firstAvailableWithdrawalNonce[withdrawal.asset] !==
                withdrawal.nonce;

              const beingWithdrawn =
                currentWithdraw.asset === withdrawal.asset &&
                currentWithdraw.nonce === withdrawal.nonce;

              return (
                <div
                  className="mb-4 flex justify-between items-center"
                  key={`${withdrawal.asset}-${withdrawal.startTime}`}
                >
                  <Typography
                    type="body"
                    variant="small"
                    className="text-text-primary inline-flex items-center gap-1"
                  >
                    {
                      +toFixedWithoutRounding(
                        formatEther(withdrawal.expectedAssetAmount),
                        6
                      )
                    }{" "}
                    <Typography
                      type="body"
                      variant="small"
                      className="text-text-tertiary"
                    >
                      {withdrawal.symbol}
                    </Typography>
                  </Typography>
                  {isClaimable && hasWithdrawDelayLapsed && (
                    <Button
                      variant="outline"
                      size="small"
                      className="w-20 px-4 py-2 text-xs font-semibold  "
                      disabled={
                        isDisabled ||
                        beingWithdrawn ||
                        (isConnected && IS_STAKING_PAUSED)
                      }
                      onClick={() =>
                        !isDisabled
                          ? handleClick(
                              +toFixedWithoutRounding(
                                formatEther(withdrawal.expectedAssetAmount),
                                6
                              ),
                              withdrawal.asset,
                              withdrawal.nonce
                            )
                          : {}
                      }
                    >
                      {beingWithdrawn ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-text-secondary"></div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          Claim
                          {isDisabled && (
                            <Tooltip
                              label={
                                <div>
                                  Please withdraw the available claim for{" "}
                                  {withdrawal.symbol} above first.
                                </div>
                              }
                            />
                          )}
                        </div>
                      )}
                    </Button>
                  )}
                  {!(isClaimable && hasWithdrawDelayLapsed) && (
                    <Typography
                      type="body"
                      variant="xsmall"
                      className={
                        getWithdrawalTimeEstimate(withdrawal).isInstant
                          ? "text-text-secondary font-medium"
                          : "text-text-tertiary"
                      }
                    >
                      {getWithdrawalTimeEstimate(withdrawal).text}
                    </Typography>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    );
  };

  const renderCompleteWithdrawals = useMemo(() => {
    return (
      <div className="min-h-[200px]">
        {isFetchingCompletedWithdrawals ? (
          <div
            className={`h-[200px] flex flex-col items-center justify-center text-center gap-4`}
          >
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : (
          <TnxHistory tnxData={completedWithdrawals} />
        )}
      </div>
    );
  }, [
    completedWithdrawals,
    isFetchingCompletedWithdrawals,
    isWithdrawSupported,
  ]);

  const renderNoUnstakeRequest = (title?: string, desc?: string, h = 440) => {
    return (
      <div
        className={`${
          isWithdrawSupported ? `h-[${h}px]` : "h-[336px]"
        } flex flex-col items-center justify-center text-center gap-4`}
      >
        {isFetching ? (
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        ) : (
          <>
            {title && (
              <Typography
                type="heading"
                variant="small"
                className="text-text-primary font-normal text-base"
              >
                {title}
              </Typography>
            )}
            {desc && (
              <Typography
                type="body"
                variant="medium"
                className="text-text-primary"
              >
                {desc}
              </Typography>
            )}
          </>
        )}
      </div>
    );
  };

  const renderConnectWallet = () => {
    return (
      <div
        className={`${
          isConnected ? "h-[440px]" : "h-[206px]"
        } flex flex-col items-center justify-center text-center gap-4`}
      >
        <Typography type="heading" variant="xxsmall">
          Connect your wallet
        </Typography>
        <Typography
          type="body"
          variant="xsmall"
          className="text-text-primary text-[10px]"
        >
          You will be able to claim your tokens after you connect your wallet
        </Typography>
      </div>
    );
  };

  const renderSwitchChainCta = () => {
    return (
      <Button
        variant="primary"
        className="rounded-xl p-4 h-14 mt-3 w-full"
        onClick={() => switchChain({ chainId: withdrawEnabledChains[0].id })}
      >
        <Typography
          type="body"
          variant="medium"
          className="text-bg-card font-semibold"
        >
          {`Switch to ${withdrawEnabledChains[0].name} to Withdraw`}
        </Typography>
      </Button>
    );
  };

  const renderConnectWalletCta = () => {
    return (
      <Button
        variant="primary"
        className="rounded-xl p-4 h-14 mt-3 w-full"
        onClick={openConnectModal}
      >
        <Typography
          type="body"
          variant="medium"
          className="text-bg-card font-semibold"
        >
          Connect Wallet
        </Typography>
      </Button>
    );
  };

  const renderWithdrawals = () => {
    if (isConnected && !showCompleted && userWithdrawals.length) {
      return renderPendingWithdrawal();
    }
    if (isConnected && showCompleted && completedWithdrawals.length) {
      return renderCompleteWithdrawals;
    }
    if (isConnected) {
      if (!completedWithdrawals.length) {
        return renderNoUnstakeRequest("No withdrawals yet", undefined, 150);
      } else {
        return renderNoUnstakeRequest(
          "No unstake requests found",
          "You will be able to claim your tokens after the Unstake request has been processed. To Unstake your tokens go to Unstake tab"
        );
      }
    }
    return renderConnectWallet();
  };

  const renderActiveCompletedTabs = () => {
    return (
      <div className="pb-10">
        <div className="border border-border-secondary rounded-xl overflow-hidden w-fit relative p-1">
          <div
            className={`absolute top-0 bottom-0 bg-text-primary rounded-lg transition-all duration-200 ease-in-out m-1 ${
              showCompleted ? "left-[40%] right-0" : "left-0 right-[55%]"
            }`}
          />

          <button
            onClick={() => handleTabToggle(TABS.active)}
            className={`relative z-10 rounded-lg px-4 py-2 transition-colors ${
              !showCompleted ? "text-white" : "text-text-primary"
            } bg-transparent hover:bg-transparent`}
          >
            <Typography
              type="body"
              variant="medium"
              className={!showCompleted ? "text-white" : "text-text-primary"}
            >
              {TABS.active}
            </Typography>
          </button>

          <button
            onClick={() => handleTabToggle(TABS.completed)}
            className={`relative z-10 rounded-lg px-4 py-2 transition-colors ${
              showCompleted ? "text-white" : "text-text-primary"
            } bg-transparent hover:bg-transparent`}
          >
            <Typography
              type="body"
              variant="medium"
              className={showCompleted ? "text-white" : "text-text-primary"}
            >
              {TABS.completed}
            </Typography>
          </button>
        </div>
      </div>
    );
  };

  const renderUpgradeOverlay = () => {
    return (
      <div className="absolute inset-0 flex items-center justify-center z-10">
        <div className="bg-bg-card border border-border-secondary rounded-xl p-6 mx-4 text-center shadow-lg">
          <Typography
            type="heading"
            variant="xsmall"
            className="text-text-primary mb-2"
          >
            Upgrade in Progress
          </Typography>
          <Typography
            type="body"
            variant="small"
            className="text-text-secondary"
          >
            Withdrawals are temporarily disabled. Check back soon.
          </Typography>
        </div>
      </div>
    );
  };

  // Check if content should be blurred due to upgrade or unsupported chain
  const shouldBlurForUpgrade = IS_CONTRACT_UPGRADE_IN_PROGRESS && isConnected;
  const shouldBlurForChain = !isWithdrawSupported && isConnected;
  const shouldBlur = shouldBlurForUpgrade || shouldBlurForChain;

  return (
    <div className="relative w-full">
      <div
        className={`relative w-full ${
          shouldBlur ? "pointer-events-none blur-sm" : "pointer-events-auto"
        }`}
      >
        {isConnected &&
          isWithdrawSupported &&
          !isFetchingCompletedWithdrawals &&
          completedWithdrawals.length > 0 &&
          renderActiveCompletedTabs()}
        {/* Refactored code with existing logic */}
        {renderWithdrawals()}
      </div>
      {shouldBlurForUpgrade && renderUpgradeOverlay()}
      {!shouldBlurForUpgrade && !isWithdrawSupported && isConnected && renderSwitchChainCta()}
      {!isConnected && renderConnectWalletCta()}
    </div>
  );
};

export default Withdraw;
